<template>
  <div class="detail-page">
    <div class="top-actions">
      <b-button variant="secondary" size="sm" @click="$router.push({ name: 'booking_customer_list_tbn' })">
        Back to bookings
      </b-button>
    </div>

    <div v-if="loading" class="loading-wrap">
      <b-spinner />
    </div>

    <div v-else-if="booking" class="detail-grid">
      <div class="detail-card">
        <div class="card-top">
          <div>
            <div class="booking-id">Booking #{{ booking.booking_id }}</div>
            <div class="trip-name" v-if="booking.group_name">{{ booking.group_name }}</div>
            <div class="org-name">{{ booking.org_name }}</div>
          </div>
          <div class="badge-stack">
            <span v-if="booking.confirmation_status" class="status-pill" :class="booking.confirmation_status">{{ booking.confirmation_status }}</span>
            <span class="status-pill" :class="booking.status">{{ booking.status }}</span>
            <span class="payment-badge" :class="paymentClass(booking.payment_status)" v-if="booking.payment_status">
              {{ booking.payment_status }}
            </span>
          </div>
        </div>

        <div class="overview-grid">
          <div class="overview-item"><div class="ov-label">Start</div><div class="ov-value">{{ formatDateTime(booking.start_time) }}</div></div>
          <div class="overview-item"><div class="ov-label">Finish</div><div class="ov-value">{{ formatDateTime(booking.finish_time) }}</div></div>
          <div class="overview-item"><div class="ov-label">Route</div><div class="ov-value">{{ booking.route_name || 'N/A' }}</div></div>
          <div class="overview-item"><div class="ov-label">Contact</div><div class="ov-value">{{ booking.contact_name || 'N/A' }}</div></div>
        </div>

        <div class="legs-section" v-if="booking.legs && booking.legs.length">
          <div class="legs-title" @click="legsExpanded = !legsExpanded">
            Trip Legs ({{ booking.legs.length }})
            <span class="legs-toggle">{{ legsExpanded ? '▲ Hide' : '▼ Show' }}</span>
          </div>
          <div v-if="legsExpanded" v-for="(leg, idx) in booking.legs" :key="idx" class="leg-row">
            <div class="leg-num">Leg {{ idx + 1 }}</div>
            <div class="leg-body">
              <div class="leg-route">
                <span class="leg-loc">{{ leg.pickup_location || '?' }}</span>
                <span class="leg-arrow"> → </span>
                <span class="leg-loc">{{ leg.dropoff_location || '?' }}</span>
              </div>
              <div class="leg-meta">
                <span>{{ formatDateTime(leg.start_time) }} – {{ formatDateTime(leg.finish_time) }}</span>
              </div>
              <div class="leg-driver" v-if="leg.driver_name || leg.vehicle_id">
                <span class="driver-avatar small-avatar" v-if="leg.driver_name">{{ driverInitials(leg.driver_name) }}</span>
                <span>{{ leg.driver_name || 'TBD' }}</span>
                <span v-if="leg.vehicle_id"> · Vehicle {{ leg.vehicle_id }}</span>
                <span v-if="leg.driver_phone"> · 📞 {{ leg.driver_phone }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="driver-card" v-else-if="booking.driver_name">
          <div class="driver-avatar">{{ driverInitials(booking.driver_name) }}</div>
          <div class="driver-info">
            <div class="driver-name">{{ booking.driver_name }}</div>
            <div class="driver-sub">Driver · {{ booking.vehicle_id || 'Vehicle TBD' }}</div>
            <div class="driver-phone" v-if="booking.driver_phone">📞 {{ booking.driver_phone }}</div>
          </div>
        </div>
        <div class="line" v-else><b>Vehicle:</b> {{ booking.vehicle_id || 'TBD' }}</div>
      </div>

      <div class="map-card">
        <div class="map-title">Vehicle location</div>
        <div v-if="mapLoading" class="map-state">
          <b-spinner style="width: 1.5rem; height: 1.5rem;" /> Loading…
        </div>
        <div v-else-if="mapMarkers.length === 0" class="map-state">
          No live GPS data available for this booking right now.
        </div>
        <GmapMap
          v-else
          ref="mapRef"
          :zoom="mapZoom"
          :center="mapCenter"
          :options="themed_map_options"
          style="width: 100%; height: 65vh; border-radius: 6px;"
        >
          <GmapMarker
            v-for="(marker, idx) in mapMarkers"
            :key="idx"
            :position="marker.position"
            :title="marker.title"
            :label="marker.label"
            :icon="marker.icon"
          />
        </GmapMap>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { tbn_get_booking_customer_detail, tbn_get_booking_customer_map } from '../networking/tbn_apis'
import bookingCustomerHelpers from '../mixins/booking_customer_helpers'
import { DARK_MAP_STYLES } from '../assets/dark_map_styles'

export default {
  name: 'booking_customer_detail_tbn',
  mixins: [bookingCustomerHelpers],
  data() {
    return {
      loading: false,
      booking: null,
      legsExpanded: false,
      mapLoading: false,
      mapMarkers: [],
      mapCenter: { lat: 39.9, lng: -97.1 },
      mapZoom: 5,
      mapTimer: null,
      mapStyle: {
        styles: [
          { featureType: 'landscape', stylers: [{ color: '#F3F4F9' }] },
          { featureType: 'water', stylers: [{ color: '#C1CDE4' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
          { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#FFFFFF' }] },
          { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#FEF8D6' }] },
        ],
      },
    }
  },
  computed: {
    ...mapState({
      user: (state) => state.profile.user,
    }),
    // the light mapStyle in data stays untouched; midnight users get the
    // dark basemap (in-shell map pages only -- standalone maps stay light)
    themed_map_options() {
      const midnight = (this.$store.state.profile || {}).site_theme === 'midnight'
      return midnight ? { ...this.mapStyle, styles: DARK_MAP_STYLES } : this.mapStyle
    },
  },
  async created() {
    await this.loadData()
  },
  beforeDestroy() {
    if (this.mapTimer) clearInterval(this.mapTimer)
  },
  watch: {
    '$route.params.tbn_booking_hash': {
      async handler() {
        if (this.mapTimer) clearInterval(this.mapTimer)
        await this.loadData()
      },
    },
  },
  methods: {
    async loadData() {
      const bookingHash = this.$route.params.tbn_booking_hash
      if (!bookingHash) return
      try {
        this.loading = true
        const { value: { data = {} } } = await tbn_get_booking_customer_detail(this.user.tenant, bookingHash)
        this.booking = data.booking || null
        if (this.booking) {
          await this.loadMapData()
          if (this.mapTimer) clearInterval(this.mapTimer)
          this.mapTimer = setInterval(() => this.loadMapData(), 10000)
        }
      } catch (e) {
        this.$emit('showAlert', 'Unable to load booking details. Please try again.', 'danger', 5000)
      } finally {
        this.loading = false
      }
    },
    async loadMapData() {
      try {
        this.mapLoading = this.mapMarkers.length === 0
        const { value: { data = {} } } = await tbn_get_booking_customer_map(
          this.user.tenant,
          { status: 'active', booking_search: this.booking.booking_id }
        )
        const results = data.results || []
        this.mapMarkers = results.map((r, idx) => ({
          position: { lat: Number(r.lat), lng: Number(r.lng) },
          title: [r.name, r.route_name].filter(Boolean).join(' – '),
          label: { text: r.name || '', className: 'tracking-label' },
          icon: {
            path: 'M0,0 L-6,14 L0,10 L6,14 Z',
            scale: 1.5,
            strokeColor: 'white',
            fillColor: '#29C68A',
            fillOpacity: 1,
            rotation: 0,
          },
        }))
        if (this.mapMarkers.length > 0) {
          this.mapCenter = this.mapMarkers[0].position
          this.mapZoom = this.mapMarkers.length === 1 ? 12 : 8
        }
      } catch (e) {
        console.warn('loadMapData failed:', e)
      } finally {
        this.mapLoading = false
      }
    },
  },
}
</script>

<style scoped>
.detail-page { margin: 16px; }
.top-actions { text-align: left; margin-bottom: 10px; }
.detail-grid { display: grid; grid-template-columns: 0.95fr 1.05fr; gap: 12px; }
.detail-card, .map-card, .empty-card { border: 1px solid #d9dce3; border-radius: 8px; background: white; padding: 14px; text-align: left; }
.card-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; margin-bottom: 14px; }
.booking-id { font-weight: 700; font-size: 16px; color: #1a2533; }
.trip-name { font-size: 13px; font-weight: 600; color: #2d3a4a; margin-top: 3px; }
.org-name { font-size: 12px; color: #5b6a79; margin-top: 2px; }
.badge-stack { display: flex; flex-direction: column; gap: 4px; align-items: flex-end; }
.status-pill { text-transform: uppercase; font-size: 10px; padding: 3px 8px; border-radius: 10px; font-weight: 700; color: white; white-space: nowrap; }
.status-pill.active { background: #198754; }
.status-pill.upcoming { background: #0d6efd; }
.status-pill.completed { background: #6c757d; }
.status-pill.confirmed { background: #198754; }
.payment-badge { font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 10px; white-space: nowrap; }
.payment-paid { background: #d1e7dd; color: #0a3622; }
.payment-due { background: #f8d7da; color: #58151c; }
.payment-deposit { background: #fff3cd; color: #664d03; }
.payment-other { background: #e9ecef; color: #495057; }
.overview-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 14px; padding: 10px; background: #f8f9fb; border-radius: 8px; }
.ov-label { font-size: 11px; color: #6c757d; font-weight: 600; text-transform: uppercase; }
.ov-value { font-size: 13px; font-weight: 600; color: #2d3a4a; margin-top: 2px; }
.driver-card { display: flex; gap: 12px; align-items: center; padding: 10px; background: #f8f9fb; border-radius: 8px; margin-top: 4px; }
.driver-avatar { background: #0d6efd; color: white; font-size: 14px; font-weight: 700; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.driver-name { font-weight: 700; font-size: 14px; }
.driver-sub { font-size: 12px; color: #6c757d; }
.driver-phone { font-size: 12px; color: #0d6efd; margin-top: 2px; }
.legs-section { margin-top: 12px; border-top: 1px solid #e9ecef; padding-top: 10px; }
.legs-title { font-weight: 700; font-size: 14px; margin-bottom: 8px; color: #1a2533; cursor: pointer; display: flex; justify-content: space-between; align-items: center; user-select: none; }
.legs-title:hover { color: #0d6efd; }
.legs-toggle { font-size: 11px; font-weight: 600; color: #0d6efd; }
.leg-row { display: flex; gap: 10px; margin-bottom: 8px; padding: 8px 10px; background: #f8f9fb; border-radius: 7px; }
.leg-num { font-weight: 700; font-size: 12px; color: #6c757d; min-width: 40px; padding-top: 2px; }
.leg-body { flex: 1; }
.leg-route { font-size: 13px; font-weight: 600; color: #2d3a4a; }
.leg-loc { }
.leg-arrow { color: #adb5bd; }
.leg-meta { font-size: 12px; color: #6c757d; margin-top: 2px; }
.leg-driver { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #495057; margin-top: 4px; }
.small-avatar { background: #0d6efd; color: white; font-size: 9px; font-weight: 700; width: 20px; height: 20px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.line { margin-bottom: 6px; font-size: 14px; }
.map-title { font-weight: 700; margin-bottom: 8px; }
.map-state { padding: 32px; text-align: center; color: #5b6a79; font-size: 14px; display: flex; align-items: center; justify-content: center; gap: 10px; }
.loading-wrap { padding: 24px; }
@media (max-width: 1100px) {
  .detail-grid { grid-template-columns: 1fr; }
  .overview-grid { grid-template-columns: 1fr 1fr; }
}
</style>
